import React, { useState, useMemo } from "react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { tryFormatMoney } from "../../../Helpers";

interface IC170DetailsDataProps {
  c170: any[];
}

const C170DetailsData: React.FC<IC170DetailsDataProps> = ({ c170 = [] }) => {
  const [selectedRow, setSelectedRow] = useState<number | null>(null);

  const headers = useMemo(() => [
    { id: "num_item", label: "Nº" },
    { id: "cod_item", label: "CÓDIGO" },
    { id: "descr_compl", label: "DESCRIÇÃO" },
    { id: "qtd", label: "QUANTIDADE" },
    { id: "unid", label: "UNIDADE" },
    { id: "vl_item", label: "VALOR DO ITEM", isMoney: true },
    { id: "vl_desc", label: "VALOR DO DESCONTO", isMoney: true },
    { id: "ind_mov", label: "IND MOV" },
    { id: "cst_icms", label: "CST ICMS" },
    { id: "cfop", label: "CFOP" },
    { id: "cod_nat", label: "COD NATUREZA" },
    { id: "vl_bc_icms", label: "VALOR BC ICMS", isMoney: true },
    { id: "aliq_icms", label: "ALÍQUOTA ICMS" },
    { id: "vl_icms", label: "VALOR DO ICMS", isMoney: true },
    { id: "vl_bc_icms_st", label: "VALOR DA BC DO ICMS", isMoney: true },
    { id: "aliq_st", label: "ALÍQUOTA ST" },
    { id: "vl_icms_st", label: "VALOR DO ICMS ST", isMoney: true },
    { id: "ind_apur", label: "IND APURAÇÃO" },
    { id: "cst_ipi", label: "CST IPI" },
    { id: "cod_enq", label: "COD ENQUADRAMENTO" },
    { id: "vl_bc_ipi", label: "VALOR BC IPI", isMoney: true },
    { id: "aliq_ipi", label: "ALÍQUOTA IPI" },
    { id: "vl_ipi", label: "VALOR IPI", isMoney: true },
    { id: "cst_pis", label: "CST PIS" },
    { id: "vl_bc_pis", label: "VALOR BC PIS", isMoney: true },
    { id: "aliq_pis_perc", label: "ALÍQ. PIS %" },
    { id: "quant_bc_pis", label: "QTD BC PIS" },
    { id: "aliq_pis_reais", label: "ALÍQ. PIS R$" },
    { id: "vl_pis", label: "VALOR DO PIS", isMoney: true },
    { id: "cst_cofins", label: "CST COFINS" },
    { id: "vl_bc_cofins", label: "VALOR BC COFINS", isMoney: true },
    { id: "aliq_cofins_perc", label: "ALÍQ. COFINS %" },
    { id: "quant_bc_cofins", label: "QTD BC COFINS" },
    { id: "aliq_cofins_reais", label: "ALÍQ. COFINS R$" },
    { id: "vl_cofins", label: "VALOR DO COFINS", isMoney: true },
    { id: "cod_cta", label: "COD CTA" },
    { id: "vl_abat_nt", label: "ABATIMENTOS NÃO TRIBUTADOS", isMoney: true }
  ], []);

  if (!c170.length) return null;

  return (
    <Grid item xs={12}>
      <Typography
        variant="h6"
        sx={{
          textTransform: "uppercase",
          color: "rgb(148 148 148)",
          my: 4
        }}
      >
        Itens da nota fiscal
      </Typography>

      <TableContainer
        component={Paper}
        sx={{ 
          maxHeight: 400,
          overflowX: 'auto',
          overflowY: 'auto'
        }}
        aria-label="Tabela de Itens C170"
      >
        <Table stickyHeader size="small" sx={{ minWidth: 2000 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: '50px', color: "rgb(148 148 148)", fontWeight: 'bold' }}>
                #
              </TableCell>
              {headers.map((header) => (
                <TableCell
                  key={header.id}
                  sx={{ 
                    color: "rgb(148 148 148)", 
                    whiteSpace: "nowrap",
                    fontWeight: 'bold',
                    minWidth: header.isMoney ? '120px' : '100px'
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {c170.map((item, index) => (
              <TableRow
                key={index}
                hover
                onClick={() => setSelectedRow(index)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: selectedRow === index ? "#f5f5f5" : "inherit"
                }}
              >
                <TableCell 
                  align="center" 
                  sx={{ 
                    width: '50px',
                    fontWeight: 500,
                    color: 'text.secondary'
                  }}
                >
                  {index + 1}
                </TableCell>
                {headers.map(({ id, isMoney }) => (
                  <TableCell
                    key={id}
                    sx={{ 
                      textAlign: isMoney ? "right" : "left",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {isMoney
                      ? tryFormatMoney(item[id])
                      : item[id] ?? ""}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default C170DetailsData;
